"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { useTranslations } from "use-intl";

import {
  ChevronRightIcon,
  CloseIcon,
  GroupIcon,
  LogoutIcon,
  PersonIcon,
  SettingsIcon,
  getListIcon as resolveListIcon,
} from "@/components/appIcons";
import { Skeleton } from "@/components/ui/skeleton";
import { useLists } from "@/contexts/lists-context";
import { authClient } from "@/lib/auth-client";
import { detachCurrentBrowserPush } from "@/lib/detachCurrentBrowserPush";
import { buildSignupHrefWithRedirect } from "@/lib/redirect-after-login";
import { cn } from "@/lib/utils";
import { clearSession } from "@/utils/session";
import { isSyntheticEmail } from "@garimpa/lib/isSyntheticEmail";

function defaultListIcon(type: string, icon: string | null): string {
  if (icon) return icon;
  if (type === "collection") return "library_music";
  if (type === "wishlist") return "favorite";
  return "queue_music";
}

function getListHref(list: { id: string }): string {
  return `/lists/${list.id}`;
}

function getListIconColor(type: string): string {
  if (type === "collection") return "text-primary";
  if (type === "wishlist") return "text-red-400";
  return "text-muted-foreground";
}

interface MobileMenuProps {
  session: {
    user: {
      name?: string | null;
      email: string;
      username?: string | null;
      isAnonymous: boolean;
    };
  };
  open: boolean;
  onClose: () => void;
}

export function MobileMenu({ session, open, onClose }: MobileMenuProps) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const t = useTranslations("globals.auth");
  const ta = useTranslations("globals.accessibility");
  const tProfile = useTranslations("globals.profile");
  const tLists = useTranslations("globals.lists");
  const tSettings = useTranslations("globals.settings");
  const tCommunity = useTranslations("globals.community");
  const { allLists, isLoaded } = useLists();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const navigatingRef = useRef(false);
  const isGuest = session.user.isAnonymous;

  useEffect(() => {
    if (!open) return;

    navigatingRef.current = false;
    document.body.style.overflow = "hidden";
    history.pushState({ mobileMenu: true }, "");
    let closedByPopState = false;

    const handlePopState = () => {
      closedByPopState = true;
      onCloseRef.current();
    };
    window.addEventListener("popstate", handlePopState);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("popstate", handlePopState);
      if (!closedByPopState && !navigatingRef.current) {
        history.back();
      }
    };
  }, [open]);

  if (!open) return null;

  const navigate = (path: string) => {
    navigatingRef.current = true;
    onClose();
    router.navigate({ to: path });
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex flex-col bg-card animate-in fade-in duration-150">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <div className="flex flex-col">
          <span className="text-sm font-semibold text-foreground">{session.user.name}</span>
          {isGuest && <span className="text-xs text-primary">{t("guest")}</span>}
          {!isSyntheticEmail(session.user.email) && (
            <span className="text-xs text-muted-foreground">{session.user.email}</span>
          )}
        </div>
        <button
          type="button"
          aria-label={ta("close")}
          onClick={onClose}
          className="relative after:absolute after:-inset-0.5 after:content-[''] flex items-center justify-center size-10 rounded-full hover:bg-secondary transition-colors"
        >
          <CloseIcon className="text-xl" />
        </button>
      </div>

      <div className="flex flex-col flex-1 overflow-y-auto">
        {/* Lists section */}
        <div className="px-5 pt-4 pb-2">
          <button
            onClick={() => navigate("/lists")}
            className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1 hover:text-foreground transition-colors"
          >
            {tLists("title")}
            <ChevronRightIcon className="text-sm" />
          </button>
        </div>
        <nav className="flex flex-col px-3 pb-2">
          {!isLoaded ? (
            <div className="flex flex-col gap-1 px-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 py-3">
                  <Skeleton className="size-5 rounded" />
                  <Skeleton className="h-4 w-32 rounded" />
                </div>
              ))}
            </div>
          ) : allLists.length > 0 ? (
            allLists.map((list) => {
              const Icon = resolveListIcon(defaultListIcon(list.type, list.icon));
              return (
                <button
                  key={list.id}
                  onClick={() => navigate(getListHref(list))}
                  className="flex items-center gap-4 px-4 py-3.5 rounded-lg text-sm hover:bg-secondary transition-colors"
                >
                  <Icon className={cn("text-xl w-6 shrink-0", getListIconColor(list.type))} />
                  <span className="flex-1 text-left truncate">{list.name}</span>
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {list.itemCount}
                  </span>
                </button>
              );
            })
          ) : (
            <p className="px-4 py-3 text-sm text-muted-foreground">{tLists("emptyList")}</p>
          )}
        </nav>

        {/* Divider + Sellers + My Profile + Settings */}
        <div className="border-t border-border mx-3" />
        <nav className="flex flex-col px-3 py-2">
          <button
            onClick={() => navigate("/community")}
            className="flex items-center gap-4 px-4 py-3.5 rounded-lg text-sm hover:bg-secondary transition-colors"
          >
            <GroupIcon className="text-xl text-muted-foreground w-6 shrink-0" />
            <span className="flex-1 text-left">{tCommunity("title")}</span>
          </button>
          {isGuest ? (
            <button
              onClick={() => navigate(buildSignupHrefWithRedirect(router.state.location.href))}
              className="flex items-center gap-4 px-4 py-3.5 rounded-lg text-sm text-primary hover:bg-secondary transition-colors"
            >
              <PersonIcon className="text-xl w-6 shrink-0" />
              <span className="flex-1 text-left">{t("saveGuestActivity")}</span>
            </button>
          ) : (
            <button
              onClick={() =>
                navigate(
                  session.user.username
                    ? `/u/${session.user.username}`
                    : "/settings?setup=username",
                )
              }
              className="flex items-center gap-4 px-4 py-3.5 rounded-lg text-sm hover:bg-secondary transition-colors"
            >
              <PersonIcon className="text-xl text-muted-foreground w-6 shrink-0" />
              <span className="flex-1 flex items-center justify-between">
                <span>{tProfile("myProfile")}</span>
                {session.user.username && (
                  <span className="text-xs text-muted-foreground">/u/{session.user.username}</span>
                )}
              </span>
            </button>
          )}
          <button
            onClick={() => navigate("/settings")}
            className="flex items-center gap-4 px-4 py-3.5 rounded-lg text-sm hover:bg-secondary transition-colors"
          >
            <SettingsIcon className="text-xl text-muted-foreground w-6 shrink-0" />
            <span className="flex-1 text-left">{tSettings("settings")}</span>
          </button>
        </nav>
      </div>

      <div className="px-3 py-4 border-t border-border">
        <button
          onClick={async () => {
            if (isGuest && !window.confirm(t("discardGuestConfirm"))) return;
            try {
              await detachCurrentBrowserPush();
            } catch {
              toast.error(t("signOutError"));
              return;
            }
            const result = isGuest
              ? await authClient.deleteAnonymousUser()
              : await authClient.signOut();
            if (result.error) {
              toast.error(
                result.error.message ?? (isGuest ? t("discardGuestError") : t("signOutError")),
              );
              return;
            }
            clearSession(queryClient);
            onClose();
            await router.navigate({ to: "/" });
          }}
          className="flex items-center gap-4 px-4 py-4 w-full rounded-lg text-sm text-destructive hover:bg-destructive/10 transition-colors"
        >
          <LogoutIcon className="text-xl" />
          {isGuest ? t("discardGuest") : t("signOut")}
        </button>
      </div>
    </div>,
    document.body,
  );
}
